import type { TimedEffectRecord } from '../domain/encounter.ts';
import type { RuntimeState } from '../domain/runtime.ts';
import type { ConditionContent, ConditionDefinition } from '../content/conditions.ts';
import { getCondition, instantiateCondition } from '../content/conditions.ts';
import { addTimedEffect, removeTimedEffect } from '../rules/encounter.ts';

/**
 * Applying and clearing Buffs and Debuffs.
 *
 * Thin composition: it builds an `Effect` from content and hands it to the
 * ordinary encounter machinery. Nothing here rolls, mitigates or modifies.
 */

export type ApplyConditionOutcome =
  | { ok: true; runtime: RuntimeState; effectId: string }
  | { ok: false; reason: string };

/**
 * Conditions are applied to the crawler, not to a fight. Most of Table 8
 * outlives combat, so this works whether or not an encounter is in progress.
 */
export function applyCondition(
  runtime: RuntimeState,
  conditionId: string,
  newId: () => string,
  content: ConditionContent,
): ApplyConditionOutcome {
  const definition = getCondition(content, conditionId);
  if (!definition) return { ok: false, reason: `Unknown condition "${conditionId}".` };

  const already = runtime.active.filter((r) => r.effect.sourceId === conditionId);

  // RULE DCC-COND-008 - Rules p.45: "Gaining a Major Injury a second time
  // changes it to a Long-Term Major Injury." The original is replaced, not
  // stacked alongside its own escalation.
  //
  // PROJECT RULING for what the source leaves silent - the THIRD and later.
  // Table 8 gives two injury tiers and no more, marks other Debuffs
  // "Stackable" where they stack, and marks neither injury so. A third
  // application therefore finds the escalated state already present and
  // REFRESHES it rather than adding a second record.
  //
  // This used to produce a Long-Term Major Injury AND a fresh Major Injury
  // side by side - a -10 penalty to every Check, from a table whose worst
  // single entry is -5. The cause was narrow: `already` matched only
  // `sourceId === conditionId`, and after the second application nothing
  // carried the BASE id any more, so the escalation branch never fired again.
  //
  // Written against `escalatesTo` rather than against injury ids, so Minor
  // Injury inherits it for free and any future escalating condition does too.
  if (definition.escalatesTo) {
    const escalated = getCondition(content, definition.escalatesTo);
    if (!escalated) {
      return { ok: false, reason: `Unknown escalation target "${definition.escalatesTo}".` };
    }
    const escalatedAlready = runtime.active.filter(
      (r) => r.effect.sourceId === definition.escalatesTo,
    );

    // The escalated state already exists: there is nowhere further to go, so
    // keep ONE target record and clear everything the escalation replaced.
    //
    // THE BASE RECORDS ARE REMOVED HERE TOO, and that is a legacy-compatibility
    // fix rather than a theoretical tidy-up. Builds before this one could
    // persist `major-injury` and `long-term-major-injury` SIDE BY SIDE - the
    // second application added the escalation without the first branch ever
    // firing again - so a saved crawler can arrive already carrying -10 to
    // every Check from a table whose worst single entry is -5. Refreshing the
    // target while leaving the stale base behind would preserve that.
    //
    // Duplicate target records are collapsed to one for the same reason: they
    // are only reachable from a save this build did not write, and a
    // non-stackable condition has no reading in which two copies are correct.
    if (escalatedAlready.length > 0) {
      let next = runtime;
      for (const record of already) next = removeTimedEffect(next, record.effect.id);
      for (const record of escalatedAlready.slice(1)) {
        next = removeTimedEffect(next, record.effect.id);
      }
      const refreshed = refreshRecord(escalated, escalatedAlready[0] as TimedEffectRecord, content);
      return {
        ok: true,
        runtime: addTimedEffect(next, refreshed),
        effectId: refreshed.effect.id,
      };
    }

    if (already.length > 0) {
      let next = runtime;
      for (const record of already) next = removeTimedEffect(next, record.effect.id);
      const record = buildRecord(escalated, newId(), content);
      return { ok: true, runtime: addTimedEffect(next, record), effectId: record.effect.id };
    }
  }

  if (already.length > 0 && !definition.stackable) {
    // Not stackable: re-applying refreshes the existing one rather than adding
    // a second copy (Rules p.45 marks only some Debuffs "Stackable").
    const refreshed = refreshRecord(definition, already[0] as TimedEffectRecord, content);
    return {
      ok: true,
      runtime: addTimedEffect(runtime, refreshed),
      effectId: refreshed.effect.id,
    };
  }

  const record = buildRecord(definition, newId(), content);
  return { ok: true, runtime: addTimedEffect(runtime, record), effectId: record.effect.id };
}

function buildRecord(
  definition: ConditionDefinition,
  instanceId: string,
  content: ConditionContent,
): TimedEffectRecord {
  return {
    effect: instantiateCondition(definition, instanceId),
    endsWhen: definition.endsWhen.map((c) => ({ ...c })),
    ...(definition.recurringDamage
      ? {
          recurringDamage: {
            ...definition.recurringDamage,
            // RULE DCC-COND-001 - Rules p.46. Debuff damage bypasses DR while
            // resistances and immunities still apply.
            ignoresDamageResistance:
              definition.recurringDamage.ignoresDamageResistance ??
              content.damageBypassesDamageResistance,
          },
        }
      : {}),
  };
}

function refreshRecord(
  definition: ConditionDefinition,
  existing: TimedEffectRecord,
  content: ConditionContent,
): TimedEffectRecord {
  return { ...buildRecord(definition, existing.effect.id, content) };
}

/** How many stacks of a condition are currently in play. */
export function conditionStacks(runtime: RuntimeState, conditionId: string): number {
  return runtime.active.filter((r) => r.effect.sourceId === conditionId).length;
}

/** Removes one instance by its effect ID. */
export function clearConditionInstance(runtime: RuntimeState, effectId: string): RuntimeState {
  return removeTimedEffect(runtime, effectId);
}
